/* Shopify owns eligibility and pricing; this block only adds one complete offer. */
if (!customElements.get('product-bogo')) {
    class ProductBogo extends HTMLElement {
        connectedCallback() {
            // A theme-editor insertion can connect before its children are parsed.
            if (!this.querySelector('[data-bogo-config]')?.textContent.trim()) {
                this.readyObserver?.disconnect();
                this.readyObserver = new MutationObserver(() => {
                    if (this.querySelector('[data-bogo-config]')?.textContent.trim()) {
                        this.readyObserver.disconnect();
                        this.connectedCallback();
                    }
                });
                this.readyObserver.observe(this, { childList: true, subtree: true, characterData: true });
                return;
            }
            this.config = JSON.parse(this.querySelector('[data-bogo-config]').textContent);
            this.scope = this.closest('.productView') || this.closest('.shopify-section');
            this.button = this.querySelector('[data-bogo-add]');
            this.buttonLabel = this.querySelector('[data-bogo-button-label]');
            this.error = this.querySelector('[data-bogo-error]');
            this.recovery = this.querySelector('[data-bogo-recovery]');
            this.giftSelect = this.querySelector('[data-bogo-gift-select]');
            this.listeners?.abort();
            this.listeners = new AbortController();
            const options = { signal: this.listeners.signal };
            this.button.addEventListener('click', () => this.addOffer(), options);
            this.querySelector('[data-bogo-review-cart]')?.addEventListener('click', () => this.reviewCart(), options);
            this.scope?.addEventListener('change', () => this.update(), options);
            window.addEventListener('pageshow', (event) => {
                if (!event.persisted) return;
                this.busy = false;
                this.update();
            }, options);
            this.update();
        }

        disconnectedCallback() {
            this.readyObserver?.disconnect();
            this.listeners?.abort();
        }

        get form() {
            return this.scope?.querySelector(`form[id="product-form-${this.config.productId}"]`);
        }

        get paidVariant() {
            const picker = this.scope?.querySelector('variant-selects, variant-radios');
            // Ella leaves the last hidden ID intact after an invalid combination.
            if (picker && 'currentVariant' in picker && !picker.currentVariant) return null;
            const id = this.form?.querySelector('[name="id"]')?.value
                || picker?.currentVariant?.id || this.config.initialVariantId;
            return this.config.variants.find(variant => String(variant.id) === String(id));
        }

        get giftVariant() {
            if (this.config.sameProduct) return this.paidVariant;
            const id = this.giftSelect?.value || this.config.giftVariantId;
            return this.config.giftVariants.find(variant => String(variant.id) === String(id));
        }

        get sellingPlan() {
            return this.form ? new FormData(this.form).get('selling_plan') : null;
        }

        get available() {
            return Boolean(this.paidVariant?.available && this.giftVariant?.available
                && (!this.config.requiresSellingPlan || this.sellingPlan)
                && (this.config.sameProduct || !this.config.giftRequiresSellingPlan));
        }

        update() {
            const available = this.available;
            this.button.disabled = Boolean(this.busy || this.needsCartReview || !available);
            this.button.setAttribute('aria-busy', String(Boolean(this.busy)));
            this.buttonLabel.textContent = this.busy ? this.config.loadingLabel
                : available ? this.config.buttonLabel : this.config.unavailableLabel;
            if (this.giftSelect) this.giftSelect.disabled = Boolean(this.busy);
            const reviewButton = this.querySelector('[data-bogo-review-cart]');
            if (reviewButton) reviewButton.disabled = Boolean(this.busy);
            for (const [selector, variant] of [
                ['[data-bogo-paid-variant]', this.paidVariant],
                ['[data-bogo-gift-variant]', this.giftVariant]
            ]) {
                const label = this.querySelector(selector);
                if (label) label.textContent = variant && variant.title !== 'Default Title' ? variant.title : '';
            }
            const image = this.querySelector('[data-bogo-image]');
            if (image) image.src = this.giftVariant?.featured_image?.src || this.config.giftImage;
        }

        productFields() {
            const fields = this.form ? new FormData(this.form) : new FormData();
            // Ella places optional personalization text and file inputs outside the form.
            this.scope?.querySelectorAll('.productView-property [name^="properties["]').forEach(input => {
                if (input.disabled || (this.form && input.form === this.form)) return;
                if ((input.type === 'checkbox' || input.type === 'radio') && !input.checked) return;
                if (input.type === 'file') {
                    if (input.files[0]) fields.set(input.name, input.files[0]);
                } else {
                    fields.set(input.name, input.value);
                }
            });
            return fields;
        }

        validateProperties() {
            const controls = new Set([
                ...Array.from(this.form?.elements || []),
                ...Array.from(this.scope?.querySelectorAll('.productView-property input, .productView-property select, .productView-property textarea') || [])
            ]);
            // The offer has its own quantity; validate only the customer's item details.
            for (const control of controls) {
                if (control.name?.startsWith('properties[') && !control.disabled && !control.reportValidity()) return false;
            }
            return true;
        }

        async waitForCartApp() {
            // Opus can load after the product block. Give its public API time to initialize.
            for (let attempt = 0; attempt < 50; attempt++) {
                if (typeof window.opusRefreshCart === 'function' && typeof window.opusOpen === 'function') return;
                await new Promise(resolve => setTimeout(resolve, 100));
            }
            throw new Error(this.config.errorMessage);
        }

        async applyDiscount() {
            if (!this.config.discountCode || this.discountApplied) return;
            // Apply Shopify's shareable code to this session without navigating away
            // from the product or replacing other eligible discounts in the cart.
            const response = await fetch(`/discount/${encodeURIComponent(this.config.discountCode)}?redirect=${encodeURIComponent(this.config.cartUrl)}`, {
                credentials: 'same-origin'
            });
            if (!response.ok) throw new Error(this.config.errorMessage);
            this.discountApplied = true;
        }

        async openAppCart() {
            await this.waitForCartApp();
            const cart = await window.opusRefreshCart();
            // Opus logs refresh failures and resolves undefined; do not open stale contents.
            if (!cart || !Array.isArray(cart.items)) throw new Error(this.config.errorMessage);
            await window.opusOpen();
        }

        showCartError(error) {
            this.error.textContent = error instanceof TypeError || !error?.message ? this.config.errorMessage : error.message;
            this.error.hidden = false;
            // Both failed adds and failed drawer refreshes can leave items in the cart.
            // Recovery only opens the drawer; it never submits the products again.
            this.needsCartReview = true;
            if (this.recovery) this.recovery.hidden = false;
        }

        async reviewCart() {
            if (this.busy) return;
            this.busy = true;
            this.update();
            try {
                if (this.offerAdded) await this.applyDiscount();
                await this.openAppCart();
                if (this.offerAdded) {
                    this.error.hidden = true;
                    if (this.recovery) this.recovery.hidden = true;
                    this.needsCartReview = false;
                }
            } catch (error) {
                this.showCartError(error);
            } finally {
                this.busy = false;
                this.update();
            }
        }

        async addOffer() {
            if (this.busy || this.needsCartReview) return;
            this.error.hidden = true;
            this.error.textContent = '';
            this.update();
            if (!this.available || !this.validateProperties()) return;

            const payload = new FormData();
            const paidQuantity = Number(this.config.buyQuantity);
            const freeQuantity = Number(this.config.freeQuantity);
            if (!Number.isInteger(paidQuantity) || paidQuantity < 1
                || !Number.isInteger(freeQuantity) || freeQuantity < 1) return;

            // Combine the same selected variant so its properties and plan stay intact.
            payload.set('items[0][id]', this.paidVariant.id);
            payload.set('items[0][quantity]', this.config.sameProduct ? paidQuantity + freeQuantity : paidQuantity);
            for (const [key, value] of this.productFields()) {
                if (/^properties\[.+\]$/.test(key)) {
                    if (typeof value === 'string' || value.size > 0) payload.append(`items[0][${key.replace('[', '][')}`, value);
                } else if (key === 'selling_plan' && value) {
                    payload.set('items[0][selling_plan]', value);
                }
            }
            if (!this.config.sameProduct) {
                payload.set('items[1][id]', this.giftVariant.id);
                payload.set('items[1][quantity]', freeQuantity);
            }

            this.busy = true;
            this.offerAdded = false;
            this.discountApplied = false;
            this.update();
            try {
                const response = await fetch(this.config.addUrl, {
                    method: 'POST', body: payload,
                    credentials: 'same-origin', headers: { Accept: 'application/json' }
                });
                if (!response.ok) {
                    const result = await response.json().catch(() => ({}));
                    throw new Error(typeof result.description === 'string' ? result.description
                        : typeof result.message === 'string' ? result.message : this.config.errorMessage);
                }
                this.offerAdded = true;
                await this.applyDiscount();
                await this.openAppCart();
            } catch (error) {
                this.showCartError(error);
            } finally {
                this.busy = false;
                this.update();
            }
        }
    }
    customElements.define('product-bogo', ProductBogo);
}
